'use client'

// Compact right-rail chapter index — just 3 dots on a vertical line.
// The active dot fills lime and scales up; the active chapter's serif
// letter floats next to it. The connecting line fills as the user
// scrolls through the chapter. No background, no labels by default —
// the full label slides in on hover/active. Hidden below 1024px.

import { useEffect, useRef, useState } from 'react'

const OLIVE = '#5B7005'
const LIME  = '#9FAF08'
const EARTH_30 = 'rgba(81,50,5,0.30)'
const EARTH_15 = 'rgba(81,50,5,0.15)'

interface Props {
  chapters: { id: string; letter: string; label: string }[]
}

export default function ChapterIndex({ chapters }: Props) {
  const [active, setActive] = useState<string>(chapters[0].id)
  const [progresses, setProgresses] = useState<Record<string, number>>({})
  const rafRef = useRef(0)

  useEffect(() => {
    const compute = () => {
      const vh = window.innerHeight
      let topActive = chapters[0].id
      const next: Record<string, number> = {}
      chapters.forEach(c => {
        const el = document.getElementById(c.id)
        if (!el) { next[c.id] = 0; return }
        const r = el.getBoundingClientRect()
        const total = r.height || 1
        const scrolled = Math.min(Math.max(-r.top, 0), total)
        next[c.id] = Math.min(1, scrolled / total)
        if (r.top <= vh * 0.4 && r.bottom > vh * 0.4) {
          topActive = c.id
        }
      })
      setProgresses(next)
      setActive(topActive)
    }
    let pending = false
    const onScroll = () => {
      if (pending) return
      pending = true
      rafRef.current = requestAnimationFrame(() => {
        pending = false
        compute()
      })
    }
    compute()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      cancelAnimationFrame(rafRef.current)
    }
  }, [chapters])

  const jump = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    const y = el.getBoundingClientRect().top + window.scrollY - 80
    window.scrollTo({ top: y, behavior: 'smooth' })
  }

  return (
    <>
      <style>{`
        .tech-ci { display: none; }
        @media (min-width: 1024px) {
          .tech-ci { display: flex; }
        }
        .tech-ci {
          flex-direction: column;
          align-items: center;
          gap: 0;
        }

        .tech-ci-node {
          position: relative;
          display: flex; align-items: center; justify-content: center;
          width: 18px; height: 18px;
          cursor: pointer;
          background: transparent; border: 0; padding: 0;
        }
        .tech-ci-dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: transparent;
          border: 1px solid ${OLIVE};
          opacity: 0.6;
          transition: width 240ms ease, height 240ms ease,
                      background 240ms ease, opacity 240ms ease,
                      box-shadow 240ms ease, border-color 240ms ease;
        }
        .tech-ci-node[aria-current="true"] .tech-ci-dot {
          width: 9px; height: 9px;
          background: ${LIME};
          border-color: ${LIME};
          opacity: 1;
          box-shadow: 0 0 0 3px rgba(159,175,8,0.18), 0 0 10px rgba(159,175,8,0.55);
        }
        .tech-ci-node:hover .tech-ci-dot {
          opacity: 1;
          border-color: ${LIME};
        }

        /* Letter + label that fly out to the LEFT of the dot.
           Hidden by default; visible on active and on hover. */
        .tech-ci-meta {
          position: absolute;
          right: calc(100% + 10px);
          top: 50%;
          transform: translateY(-50%) translateX(4px);
          display: flex; align-items: center; gap: 8px;
          opacity: 0;
          pointer-events: none;
          transition: opacity 220ms ease, transform 280ms cubic-bezier(0.22,1,0.36,1);
          white-space: nowrap;
        }
        .tech-ci-node[aria-current="true"] .tech-ci-meta,
        .tech-ci-node:hover .tech-ci-meta {
          opacity: 1;
          transform: translateY(-50%) translateX(0);
        }
        .tech-ci-letter {
          font-family: var(--font-serif);
          font-style: italic;
          font-weight: 400;
          font-size: 16px;
          line-height: 1;
          color: ${OLIVE};
          letter-spacing: -0.02em;
        }
        .tech-ci-node[aria-current="true"] .tech-ci-letter { color: ${LIME}; }
        .tech-ci-label {
          font-family: var(--font-body);
          font-size: 9px;
          letter-spacing: 0.34em;
          text-transform: uppercase;
          color: rgba(81,50,5,0.55);
          font-weight: 600;
        }

        /* Connecting line between chapters — empty rail + lime fill */
        .tech-ci-rail {
          position: relative;
          width: 1px;
          height: 38px;
          background: ${EARTH_15};
          margin: 2px 0;
        }
        .tech-ci-rail > i {
          position: absolute;
          left: 0; top: 0; width: 100%;
          background: ${LIME};
          display: block;
          transition: height 180ms linear;
        }
      `}</style>

      <nav
        className="tech-ci"
        aria-label="Chapter index"
        style={{
          position: 'fixed',
          top: '50%',
          right: 'clamp(18px, 2vw, 28px)',
          transform: 'translateY(-50%)',
          zIndex: 40,
        }}
      >
        {chapters.map((c, i) => {
          const isActive = active === c.id
          const pct = (progresses[c.id] ?? 0) * 100
          return (
            <div key={c.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => jump(c.id)}
                aria-label={`Jump to chapter ${c.letter} — ${c.label}`}
                aria-current={isActive ? 'true' : undefined}
                className="tech-ci-node"
              >
                <span className="tech-ci-meta">
                  <span className="tech-ci-label">{c.label}</span>
                  <span className="tech-ci-letter">{c.letter}</span>
                </span>
                <span className="tech-ci-dot" />
              </button>
              {i < chapters.length - 1 && (
                <div className="tech-ci-rail">
                  <i style={{ height: `${pct}%` }} />
                </div>
              )}
            </div>
          )
        })}
      </nav>
    </>
  )
}
